/* eslint-disable dot-notation */
/* eslint-disable @typescript-eslint/dot-notation */
/* eslint-disable max-len */
/* eslint-disable react-hooks/exhaustive-deps */
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-nocheck
import React, { useRef, createContext } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import getAppState from 'app-state';
import {
  setState,
  setError,
  setLoading,
  setEditingControls,
  setSnackbar,
  setIsPws,
  setCartDataLoaded,
  setHideErrorModal,
  setCheckoutError,
  setHasBadCoupon,
} from '../autoshipSlice';
import { format } from '../../container/AutoshipV2/Utils/currencyFormatter';
import t from '@rcm/i18n-client';

export type CartContextType = {
  cart: StateData<Cart> | {};
  getCart: (processOrder?: boolean) => void;
  revertState: () => void;
  placeOrder: (orderInfo: StateData) => void;
  abortAllRequests: () => void;
  updateAddresses: (address: {
    shipping?: Partial<StateData<Cart>['shipping']['data']['shipAddress']>;
    billAddress?: Partial<StateData<Cart>['billAddress']['data']>;
  }) => Promise<void>;
};

export const CartContext = createContext<CartContextType | null>(null);

const ManageCartProvider = ({ children }) => {
  const dispatch = useDispatch();
  const isPws = !!getAppState('pws', false);
  const cartControllerRef = useRef(null);

  const getCart = async (signal, processOrder = false) => {
    try {
      const response = processOrder
        ? await axios.post(`/api/cart/order/current/pre-process`, { signal })
        : await axios.get(`/api/cart/order/current`, { signal });
      const currentUser = getAppState('currentUser.userId', null);
      const guestOrderingEnabled = getAppState(
        'pws.guestOrderingEnabled',
        false
      );
      const isSignupOrder =
        response.data.data.attributes?.enrollmentRules?.orderIsEnrollment;
      const isGuestOrder =
        guestOrderingEnabled && !currentUser && !isSignupOrder;
      let limitExceeded = false;

      if (localStorage.getItem('paymentError')) {
        dispatch(setError(localStorage.getItem('paymentError')));
      } else if (
        isGuestOrder &&
        response.data.data.orderTotals?.subTotal >
          response.data.data.guestOrderLimit
      ) {
        limitExceeded = true;
        dispatch(
          setError(
            t('Guest orders are limited to') +
              ' ' +
              format(response.data.data.guestOrderLimit) +
              '.'
          )
        );
      } else if (response.data?.messages?.length) {
        if (
          ![
            'cartItemsRequired',
            'ITEM_NOT_AVAILABLE_FOR_ACCOUNT_TYPE',
          ].includes(response.data?.messages[0].code)
        ) {
          dispatch(setError(response.data.messages[0].value));
        }
      } else {
        dispatch(setError(''));
        dispatch(setHasBadCoupon(false));
      }

      if (response.data?.messages) {
        response.data.messages.forEach((message) => {
          if (
            message.code === 'shipAddressRequired' ||
            message.source.includes('shipAddress')
          ) {
            dispatch(setEditingControls({ key: 'shipping', value: true }));
          } else if (
            message.code === 'billAddressRequired' ||
            message.source.includes('billAddress')
          ) {
            dispatch(
              setEditingControls({ key: 'billingAddress', value: true })
            );
          }
        });
      }
      const { items, userId } = response.data.data;
      if (currentUser != userId) {
        dispatch(
          setError(
            'You are not authorized to view this page. Please contact Support for further assistance.'
          )
        );
        dispatch(setSnackbar({ message: 'Unauthorized', type: 'error' }));
        return null;
      }
      const orderItems = items
        ?.map((item: CartItem) => ({
          ...item,
          quantity: item.qty,
          imgUrl: item.sku.mainImage.imageSrc,
          product_id: item.sku.productId,
        }))
        ?.sort((a, b) => b.product_id - a.product_id)
        ?.filter((item) => Boolean(item));
      dispatch(setCheckoutError(false));
      return {
        ...response.data.data,
        guestOrderLimitExceeded: limitExceeded,
        items: orderItems,
      };
    } catch (e) {
      if (e.response.data?.messages) {
        e.response.data.messages.forEach((message) => {
          if (
            message.code === 'shipAddressRequired' ||
            message.source.includes('shipAddress')
          ) {
            dispatch(setEditingControls({ key: 'shipping', value: true }));
          } else if (
            message.code === 'billAddressRequired' ||
            message.source.includes('billAddress')
          ) {
            dispatch(
              setEditingControls({ key: 'billingAddress', value: true })
            );
          }
        });
      }

      if (
        e.response.data?.messages?.[0]?.params?.['server-message'] ===
        'COUPON NOT FOUND'
      ) {
        dispatch(setHasBadCoupon(true));
      }
      dispatch(
        setCheckoutError(
          e.response?.data?.error?.messages[0].source === 'checkoutException' ||
            e.response?.data?.messages[0].source === 'checkoutException'
        )
      );
      if (e.code !== 'ERR_CANCELED') {
        dispatch(
          setError(
            e.response?.data?.error?.messages[0].value ??
              e.response?.data?.messages[0].value ??
              'There was an error getting Cart data.'
          )
        );
      }
    }
  };

  const getData = async (processOrder = false) => {
    const cartController = new AbortController();
    if (cartControllerRef.current?.signal) {
      cartControllerRef.current.abort();
    }
    cartControllerRef.current = cartController;

    try {
      const initialCart = await getCart(
        cartControllerRef.current.signal,
        processOrder
      );
      const userShipVia = localStorage.getItem('userSetShipVia') ?? null;
      if (userShipVia) {
        initialCart?.shipping?.data.shipVia = +userShipVia;
      } else if (initialCart?.shipping?.data.shipVia === 0) {
        initialCart?.shipping?.data.shipVia =
          initialCart?.attributes?.allowedShipMethods[0].value;
        getCheckoutOrderManager().then(async (orderManager) => {
          await orderManager.patchOrder(initialCart);
          await getData();
        });
      }

      dispatch(setState(initialCart));
      dispatch(setCartDataLoaded(true));
      dispatch(setIsPws(isPws));
    } catch (e) {
      if (e.code !== 'ERR_CANCELED') {
        dispatch(
          setError(
            e.response?.data?.error?.messages[0]?.value ??
              e.response?.data?.messages[0]?.value ??
              'There was an error getting Cart data.'
          )
        );
      }
    }
  };

  const placeOrder = async (orderInfo) => {
    dispatch(setLoading(true));
    try {
      const response = await axios.post(
        '/api/cart/order/current/process',
        orderInfo
      );
      dispatch(setLoading(false));
      window.location.href =
        '/checkout/receipt?orderNumber=' + response.data.data.orderNumber;
      return;
    } catch (e) {
      dispatch(setLoading(false));
      dispatch(setSnackbar({ message: 'Order error.', type: 'error' }));
    }
  };

  /**
   * In the case that a component is unmounted before the
   * getCart() function completes, this function will cancel
   * the requests. This is intended to be used (and generally
   * should be) in the cleanup function of the useEffect where
   * getCart() is called.
   * @example
   * useEffect(() => {
   *  getCart();
   *  return () => abortAllRequests();
   * }, [])
   */
  const abortAllRequests = () => {
    if (cartControllerRef.current) {
      cartControllerRef.current.abort();
    }
  };

  /**
   * cart: the cart response object from the api call
   * getCart: Function to make api call to fetch cart and hydrate redux
   * placeOrder: Function that sends ACTIVE redux state to backend to save, and re-hydrate redux
   * revertState: set redux to saved autoship state
   */
  return (
    <CartContext.Provider
      value={{
        getCart: getData,
        placeOrder,
        abortAllRequests,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export default ManageCartProvider;
